import React, { useEffect } from 'react';
import {
  PricesContextProvider,
  SelectorContent,
  SelectorContextProvider,
  VatModeProvider,
} from '@printdeal/product-selector';
import { transformUrl } from 'unpic';
import Layout from '../Layout';
import LayoutSection from '../layout-section/LayoutSection';
import Breadcrumb from '../Breadcrumb';
import useSegmentTracking, { EVENT_TYPE } from '../../hooks/useSegmentTracking';
import { dispatchDataLayerEvent } from '../../helpers/dataLayerHelpers/productDataLayerHelper';
import type { Paths } from '../../types/gatsby';
import type { IPageData, IPortalConfiguration, IProductPage } from '../../types/product/productDetailPage';

interface Props {
  localizedPaths: Paths;
  pageData: {
    pageContent: IProductPage & IPageData;
    portalConfiguration: IPortalConfiguration,
  };
  selector?: SelectorContent;
  pageContent: IProductPage & IPageData;
  children: React.ReactNode;
}

export const ProductPageLayout = ({
  pageData,
  localizedPaths,
  selector,
  children,
}: Props) => {
  const {
    pageContent,
    portalConfiguration: {
      currencies,
    },
  } = pageData;

  const {
    pageLayout, title, featuredImage, productCategory,
  } = pageContent;

  const featuredImageUrl = featuredImage?.url ? transformUrl({
    url: featuredImage.url,
    width: 120,
  }) : undefined;

  useSegmentTracking(EVENT_TYPE.PRODUCT_VIEWED, {
    label: title,
    currency: currencies ? currencies[0] : 'EUR',
    PrUUID: pageContent.contentful_id,
    product_id: pageContent.contentful_id,
    sku: selector?.product?.key || '',
    category: productCategory.categoryName,
    masterVariant: selector?.configuration.variantSku || '',
    masterVariantId: pageContent.contentful_id,
    name: pageContent.productName,
    image_url: featuredImageUrl?.toString(),
  });

  useEffect(() => {
    if (selector) {
      const { product, configuration } = selector;
      dispatchDataLayerEvent({
        id: product.key,
        eventLabel: {
          name: product.productNameOriginal || product.productName,
          contentfulId: pageContent.contentful_id,
        },
        productPageProductName: pageContent.productName,
        variantSKU: configuration?.variantSku,
      }, {
        eventAction: 'product_detail_view',
        eventNonInteraction: true,
      });
    }
  }, [pageContent.contentful_id, pageContent.productName, selector]);

  // Easy or PPAG Layout
  if (pageLayout === 'Easy' || pageLayout === 'PPAG') {
    return (
      <SelectorContextProvider isPimProduct={selector?.product.isPimProduct}>
        <PricesContextProvider>
          <Layout localizedPaths={localizedPaths} showStickyFooter>
            {children}
          </Layout>
        </PricesContextProvider>
      </SelectorContextProvider>
    );
  }

  // Advanced Layout
  return (
    <VatModeProvider>
      <SelectorContextProvider isPimProduct={selector?.product.isPimProduct}>
        <PricesContextProvider>
          <Layout localizedPaths={localizedPaths}>
            <LayoutSection withSidebar={false} withDivider={false}>
              <Breadcrumb className="pd-mb-6">
                <Breadcrumb.Item to={pageContent.productCategory.path}>
                  {pageContent.productCategory.categoryName}
                </Breadcrumb.Item>
                <Breadcrumb.Item isCurrentPage>{pageContent.productName || pageContent.title || ''}</Breadcrumb.Item>
              </Breadcrumb>
              {children}
            </LayoutSection>
          </Layout>
        </PricesContextProvider>
      </SelectorContextProvider>
    </VatModeProvider>
  );
};
